/**
 * The conversation model - DATA-009.
 *
 * Three shapes rather than one, matching `0003_conversation` and
 * docs/architecture/data-model-entities.md section 16.3: the channel, the topic
 * inside it, and the utterance. They have different lifetimes, which is why they
 * are not one table with nullable columns.
 *
 * Hand-written interfaces and `as const` vocabularies, like `employee.ts`: these
 * are domain shapes. A wire shape is validated on arrival and lives under
 * `src/http/`, which is API-008's to add.
 */

/**
 * Where a conversation happens. Only `WEB` is served today; the rest are in the
 * check constraint because ADR-0006 makes one workspace identity answer across
 * channels, and widening a vocabulary by migration happens in a hurry.
 *
 * `DISCORD` is here for exactly that reason rather than because anything plans
 * it - docs/architecture/data-model-entities.md lists it, and leaving it out
 * while keeping `TEAMS` would have been the arbitrary half of the argument.
 */
export const CONVERSATION_PROVIDERS = ['WEB', 'SLACK', 'TEAMS', 'DISCORD', 'API'] as const;
export type ConversationProvider = (typeof CONVERSATION_PROVIDERS)[number];

export const CONVERSATION_SCOPE_TYPES = ['DM', 'CHANNEL', 'GROUP', 'API'] as const;
export type ConversationScopeType = (typeof CONVERSATION_SCOPE_TYPES)[number];

/** Who said it. An employee and the system are both not-a-person, and differ. */
export const MESSAGE_SENDER_TYPES = ['USER', 'EMPLOYEE', 'SYSTEM'] as const;
export type MessageSenderType = (typeof MESSAGE_SENDER_TYPES)[number];

export interface Conversation {
  readonly id: string;
  readonly workspaceId: string;
  readonly provider: ConversationProvider;
  /** The provider's own id. Absent for `WEB`, which has no outside identity. */
  readonly externalId?: string;
  readonly scopeType: ConversationScopeType;
  readonly createdAt: string;
}

export interface Thread {
  readonly id: string;
  readonly conversationId: string;
  readonly workspaceId: string;
  readonly title: string;
  /** Absent when the thread began outside the product, from an inbound event. */
  readonly createdBy?: string;
  /** The employee answering in it - EMP-008. Absent until one is chosen. */
  readonly employeeId?: string;
  readonly createdAt: string;
}

export interface Message {
  readonly id: string;
  readonly workspaceId: string;
  readonly threadId: string;
  readonly externalMessageId?: string;
  readonly senderType: MessageSenderType;
  /**
   * Present exactly when `senderType` is `USER` - the database holds the two
   * consistent through `message_sender_matches_type`, so a decoded row that
   * disagrees is a schema drift rather than a case to handle.
   */
  readonly senderUserId?: string;
  readonly content: Readonly<Record<string, unknown>>;
  readonly createdAt: string;
}
